import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  Method,
  AxiosProgressEvent,
} from "axios";
import Config from "../Config";

const getAuthToken = (): string | null => {
  return localStorage.getItem("access_token");
};

type ProgressCallback = (progressEvent: AxiosProgressEvent) => void;

interface ApiRequestOptions {
  method: Method;
  path: string;
  data?: any;
  token?: boolean;
  contentType?: string;
  progressCallback?: ProgressCallback | null;
  signal?: AbortSignal;
}

const apiRequest = async ({
  method,
  path,
  data = {},
  token = true,
  contentType = "application/json",
  progressCallback = null,
  signal,
}: ApiRequestOptions): Promise<AxiosResponse<any>> => {
  const headers: Record<string, string> = {
    // Don't set Content-Type for multipart/form-data — axios must set it
    // automatically so it includes the required boundary parameter.
    ...(contentType !== "multipart/form-data" ? { "Content-Type": contentType } : {}),
    ...(token ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
  };

  const scheme = Config.DISABLE_SSL ? "http" : "https";

  const config: AxiosRequestConfig = {
    method,
    url: `${scheme}://${Config.HOSTNAME}/api${path}`,
    headers,
    data,
    onUploadProgress: progressCallback ?? ((_: AxiosProgressEvent) => {}), // fallback empty function
    ...(signal ? { signal } : {}),
  };

  try {
    const response = await axios(config);

    if (response.status >= 200 && response.status < 300) {
      return response;
    } else {
      const error = new Error(
        `Request failed with status ${response.status}`,
      ) as any;
      error.response = response;
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

const get = (
  path: string,
  token = true,
  contentType = "application/json",
  signal?: AbortSignal,
) => apiRequest({ method: "GET", path, token, contentType, ...(signal ? { signal } : {}) });

const post = (
  path: string,
  data: any,
  token = true,
  contentType = "application/json",
  progressCallback: ProgressCallback | null = null,
) =>
  apiRequest({
    method: "POST",
    path,
    data,
    token,
    contentType,
    progressCallback,
  });

const del = (path: string, token = true, contentType = "application/json") =>
  apiRequest({ method: "DELETE", path, token, contentType });

const patch = (
  path: string,
  data: any,
  token = true,
  contentType = "application/json",
) => apiRequest({ method: "PATCH", path, data, token, contentType });

// health check endpoint
export const checkHealth = async () => {
  try {
    // use a simple endpoint that doesn't require authentication
    await get("/auth/keycloak/vars", false);
    return { healthy: true, api: true };
  } catch (error) {
    return { healthy: false, api: false };
  }
};

// auth endpoints
export const exchangeKeycloakCode = (code: string) =>
  // The code comes from URLSearchParams.get() on the callback page, which has
  // already percent-decoded it, so it can contain characters (+, &, =, /) that
  // must be re-encoded before going back into a query string.
  post(`/auth/keycloak/callback?code=${encodeURIComponent(code)}`, {}, false);
export const getCurrentUser = () => get("/auth/me");
// Updates the display name. Returns a fresh access token embedding the
// new name; the caller persists it so the token's claim stays in sync.
export const updateProfileName = (name: string) =>
  patch("/auth/me", { name });

// document endpoints
export const addDocument = (name: string, organisationId?: string) => {
  const params = new URLSearchParams({ name });
  if (organisationId) {
    params.append("organisation_id", organisationId);
  }
  return post(`/document/?${params.toString()}`, {});
};
export const addPage = (
  documentID: string,
  file: File,
  progressCallback: ProgressCallback | null = null,
  insertMode?: "start" | "after" | "end",
  insertAfterPage?: number,
) => {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams();
  if (insertMode) {
    params.append("insert_mode", insertMode);
  }
  if (insertAfterPage !== undefined && insertMode === "after") {
    params.append("insert_after_page", insertAfterPage.toString());
  }
  const queryString = params.toString();
  const url = `/document/${documentID}/pages/${queryString ? `?${queryString}` : ""}`;
  return post(url, form, true, "multipart/form-data", progressCallback);
};

export const getDocument = (documentID: string) =>
  get(`/document/${documentID}/`);
export const getDocumentPages = (documentID: string) =>
  get(`/document/${documentID}/pages/`);
export const getPageData = (documentID: string, pageID: string) =>
  get(`/document/${documentID}/pages/${pageID}/?data=true`);
export const getPageDataWithBbox = (documentID: string, pageID: string) =>
  get(`/document/${documentID}/pages/${pageID}/?data=true&include_bbox=true`);
export const getPageImage = (documentID: string, pageID: string) =>
  get(`/document/${documentID}/pages/${pageID}/?image=true`);
export const getAllPagesData = (
  documentID: string,
  options: { includeRaw?: boolean; offset?: number; limit?: number } = {},
) => {
  const params = new URLSearchParams();
  if (options.includeRaw) params.append("include_raw", "true");
  if (options.offset !== undefined) params.append("offset", String(options.offset));
  if (options.limit !== undefined) params.append("limit", String(options.limit));
  const qs = params.toString();
  return get(`/document/${documentID}/pages-data${qs ? `?${qs}` : ""}`);
};

export const deleteDocument = (documentID: string) =>
  del(`/document/${documentID}/`);
export const deletePage = (documentID: string, pageID: string) =>
  del(`/document/${documentID}/pages/${pageID}/`);
export const restructurePage = (documentID: string, pageID: string) =>
  post(`/document/${documentID}/pages/${pageID}/restructure`, {});
export const addMoleculeToPage = (
  documentID: string,
  pageID: string,
  smiles: string,
) =>
  post(`/document/${documentID}/pages/${pageID}/molecules`, { smiles });
export const mergeMoleculesIntoPage = (
  documentID: string,
  pageID: string,
  pageJson: unknown,
) =>
  post(`/document/${documentID}/pages/${pageID}/molecules/merge`, {
    page_json: pageJson,
  });
export const updateMoleculeSmiles = (
  documentID: string,
  pageID: string,
  moleculeUuid: string,
  smiles: string,
) =>
  patch(`/document/${documentID}/pages/${pageID}/molecules/${moleculeUuid}`, {
    smiles,
  });
export const deleteMolecule = (
  documentID: string,
  pageID: string,
  moleculeUuid: string,
) =>
  del(`/document/${documentID}/pages/${pageID}/molecules/${moleculeUuid}`);
export const addReactionToPage = (
  documentID: string,
  pageID: string,
  rxnSmiles: string,
) =>
  post(`/document/${documentID}/pages/${pageID}/reactions`, {
    rxn_smiles: rxnSmiles,
  });
export const updateReactionSmiles = (
  documentID: string,
  pageID: string,
  reactionUuid: string,
  rxnSmiles: string,
) =>
  patch(`/document/${documentID}/pages/${pageID}/reactions/${reactionUuid}`, {
    rxn_smiles: rxnSmiles,
  });
export const deleteReaction = (
  documentID: string,
  pageID: string,
  reactionUuid: string,
) =>
  del(`/document/${documentID}/pages/${pageID}/reactions/${reactionUuid}`);
export const renameDocument = (extractionId: string, newName: string) =>
  patch(`/document/${extractionId}/?name=${newName}`, {});

export const getJobList = (
  params: {
    limit?: number;
    offset?: number;
    search?: string;
    tag_id?: string;
    untagged?: boolean;
    sort_order?: string;
  } = {},
) => {
  const query = new URLSearchParams();
  query.append("thumbnail", "true");
  if (params.limit !== undefined) query.append("limit", String(params.limit));
  if (params.offset !== undefined)
    query.append("offset", String(params.offset));
  if (params.search) query.append("search", params.search);
  if (params.tag_id) query.append("tag_id", params.tag_id);
  if (params.untagged) query.append("untagged", "true");
  if (params.sort_order) query.append("sort_order", params.sort_order);
  return get(`/document/?${query.toString()}`);
};

// full-text search (POST /search) — see api/api/search/schemas.py

// Why a document matched (#490 hybrid search). Optional on SearchResult so a
// pre-#490 payload can't break rendering. `numeric` (#522) is a pure-numeric
// query served by a hard property restrict with no text/semantic signal.
// `reaction` (Search V4) is a role-tagged reaction-participant hit.
export type MatchType =
  | "keyword"
  | "semantic"
  | "smiles"
  | "reaction"
  | "hybrid"
  | "numeric";

// A reaction-role constraint a document satisfied: the query `smiles` and the
// `role` (`reactant`/`product`) it matched in that role, so the UI can show
// *why* a reaction hit matched (Search V4). Reused both per-result and in
// the interpreted plan's understood reaction lane.
export interface ReactionMatch {
  smiles: string;
  role: string;
}

export interface SearchResult {
  document_id: string;
  name: string;
  matched_pages: number[]; // page_order values; [] for a name-only match
  snippet: string | null; // ts_headline (<b>…</b>) when a keyword term matched; plain leading-text prefix for a no-keyword (e.g. semantic-only) hit; null for a name-only match
  matched_smiles: string | null; // SMILES matched for a SMILES-like query; null otherwise
  matched_reactions?: ReactionMatch[] | null; // {smiles, role} reaction constraints satisfied; null/absent when not a reaction hit
  match_type?: MatchType; // #490: why the document matched
  semantic_score?: number | null; // cosine similarity of the best page to the query (higher = closer); null when no semantic hit; kept for future use, not rendered
  rank: number;
}

// How a numeric constraint was compared (#522). Bounds are in the property's
// canonical unit: `ge`/`gt` set value_min, `le`/`lt` set value_max, `range`
// sets both.
export type NumericFilterComparator = "gt" | "ge" | "lt" | "le" | "range";

// A numeric constraint the backend parsed out of the query and applied as a
// hard range restrict (#522/#523). Echoed back so the UI can show *which*
// filters were applied ("Tg 140–200 °C"). Bounds are in `unit` (the property's
// canonical unit); either bound may be null for an open bound. `span_start`/
// `span_end` are the `[start, end)` char offsets of the consumed span in the
// submitted query, so the UI can excise this constraint by slice — unambiguous
// even when one span is a substring of another (#536).
export interface NumericFilter {
  property_key: string;
  display: string;
  comparator: NumericFilterComparator;
  value_min?: number | null;
  value_max?: number | null;
  unit?: string | null;
  span_start?: number | null;
  span_end?: number | null;
}

// The NL query planner's understanding of the prompt (Search V4), echoed
// so the UI can show *which* parts were understood — the natural-language
// analogue of the `numeric_filters` chips. Present in the response ONLY on the
// confident LLM-planner path; absent on the deterministic heuristic path (and
// whenever the planner flag is off), so a fallback response is byte-identical to
// today. Numeric understanding is echoed separately in `numeric_filters`, not
// duplicated here. `intent` is keyword|semantic|structure|reaction|mixed.
export interface InterpretedPlan {
  free_text: string;
  exact_tokens: string[];
  smiles: string[];
  reaction_constraints: ReactionMatch[];
  intent: string;
  confidence: number;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  limit: number;
  offset: number;
  numeric_filters?: NumericFilter[]; // #523: constraints applied to this search; optional/defensive for a pre-#522 payload
  interpreted_plan?: InterpretedPlan; // present only on the LLM-planner path; absent = heuristic fallback
}

// POST, not GET (#613): Cloud Run logs the full request URL — query string
// included — to its own platform request log, which the API's redaction can't
// reach. Sending the query in the body keeps compound names and grade codes out
// of Cloud Logging. Don't "simplify" this back to a GET with `?q=`.
export const searchDocuments = (
  query: string,
  params: { limit?: number; offset?: number } = {},
): Promise<AxiosResponse<SearchResponse>> =>
  post("/search", {
    q: query,
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.offset !== undefined ? { offset: params.offset } : {}),
  }) as Promise<AxiosResponse<SearchResponse>>;

export const getMoleculeSVG = (
  transcription: string,
  depictParams: Record<string, string> = {},
  token = false,
) => {
  const defaultParams = {
    abbr: "off",
    hdisp: "S",
    zoom: "1.3",
    annotate: "none",
    r: "0",
    ...depictParams,
  };
  const allParams = new URLSearchParams({
    smi: transcription,
    ...defaultParams,
  });
  return get(`/depict?${allParams.toString()}`, token, "application/json");
};

/** Render a molecule from its OCSR graph (atoms + bonds) → PNG blob */
export const getMoleculeRender = (graph: {
  symbols: string[];
  coords: number[][];
  edges: number[][];
  bbox_width?: number;
  bbox_height?: number;
  force_aspect?: boolean;
}) => {
  const scheme = Config.DISABLE_SSL ? "http" : "https";
  return axios.post(
    `${scheme}://${Config.HOSTNAME}/api/document/molecule/render`,
    graph,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
      responseType: "blob",
    },
  );
};

// label endpoints
export const createTag = (name: string, color: string = "#3B82F6") =>
  post("/document/tags/", { name, color });
export const getTags = () => get("/document/tags/");
export const getTag = (tagId: string) => get(`/document/tags/${tagId}/`);
export const updateTag = (tagId: string, name?: string, color?: string) =>
  patch(`/document/tags/${tagId}/`, { name, color });
export const deleteTag = (tagId: string) => del(`/document/tags/${tagId}/`);
export const addTagToDocument = (tagId: string, documentId: string) =>
  post(`/document/tags/${tagId}/documents/${documentId}/`, {});
export const removeTagFromDocument = (tagId: string, documentId: string) =>
  del(`/document/tags/${tagId}/documents/${documentId}/`);

// organization endpoints
export const getUserOrganizations = () =>
  get("/organization/user/organizations");
export const getAllOrganizations = () => get("/organization/");
export const getOrganization = (organizationId: string) =>
  get(`/organization/${organizationId}/`);
export const getOrganizationMembers = (organizationId: string) =>
  get(`/organization/${organizationId}/members`);
export const addOrganizationMember = (
  organizationId: string,
  email: string,
  role: string = "member",
) => post(`/organization/${organizationId}/members`, { email, role });
export const updateOrganizationMemberRole = (
  organizationId: string,
  memberId: string,
  role: string,
) => patch(`/organization/${organizationId}/members/${memberId}`, { role });
export const removeOrganizationMember = (
  organizationId: string,
  memberId: string,
) => del(`/organization/${organizationId}/members/${memberId}`);
export const resendMemberInvitation = (
  organizationId: string,
  memberId: string,
) =>
  post(
    `/organization/${organizationId}/members/${memberId}/resend-invitation`,
    {},
  );
// POST for the same reason as searchDocuments (#613) — the value is a partial
// email address and must not enter a URL.
export const searchUsers = (query: string) =>
  post("/organization/search-users", { query });
export const createOrganization = (name: string, adminEmail: string) =>
  post("/organization/", { name, admin_email: adminEmail });
export const updateOrganizationSettings = (
  organizationId: string,
  settings: {
    allow_mobile_upload?: boolean;
    allow_member_download?: boolean;
    hide_member_emails?: boolean;
    allow_text_sharing?: boolean;
    name?: string;
    contact_phones?: string[];
    contact_emails?: string[];
  },
) => patch(`/organization/${organizationId}/settings`, settings);
export const getOrganizationAdmin = (organizationId: string) =>
  get(`/organization/${organizationId}/admin`);
export const updateOrganizationAdminSettings = (
  organizationId: string,
  settings: {
    show_text?: boolean;
    show_molecules?: boolean;
    show_tables?: boolean;
    show_drawings?: boolean;
  },
) => patch(`/organization/${organizationId}/admin-settings`, settings);
export const getOrganizationDocuments = (
  organizationId: string,
  params: {
    limit?: number;
    offset?: number;
    search?: string;
    sort_order?: string;
  } = {},
) => {
  const query = new URLSearchParams();
  query.append("thumbnail", "true");
  if (params.limit !== undefined) query.append("limit", String(params.limit));
  if (params.offset !== undefined)
    query.append("offset", String(params.offset));
  if (params.search) query.append("search", params.search);
  if (params.sort_order) query.append("sort_order", params.sort_order);
  return get(`/document/organisation/${organizationId}?${query.toString()}`);
};

// document sharing endpoints
export const shareDocument = (documentId: string, userIds: string[]) =>
  post(`/document/${documentId}/shares`, { user_ids: userIds });

export const getDocumentShares = (documentId: string) =>
  get(`/document/${documentId}/shares`);

export const removeDocumentShare = (documentId: string, shareId: string) =>
  del(`/document/${documentId}/shares/${shareId}`);

// public text block sharing
export const createPublicTextShare = (documentId: string, blockUuid: string, text: string) =>
  post(`/document/${documentId}/text-shares`, { block_uuid: blockUuid, text });

export const getPublicTextShare = (token: string) =>
  get(`/document/text-shares/${token}`, false);

// bulk upload / admin download
export const getBulkUploadFolders = () => get("/document/bulk-upload/folders");

export const downloadBulkUserFiles = (userEmail: string) => {
  const scheme = Config.DISABLE_SSL ? "http" : "https";
  return axios.get(
    `${scheme}://${Config.HOSTNAME}/api/document/bulk-upload/download?user_email=${encodeURIComponent(userEmail)}`,
    {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
      responseType: "blob",
    },
  );
};

export const bulkUpload = (
  entries: { file: File; name: string }[],
  progressCallback: ProgressCallback | null = null,
) => {
  const form = new FormData();
  for (const { file, name } of entries) {
    form.append("files", file);
    form.append("names", name);
  }
  return post("/document/bulk-upload", form, true, "multipart/form-data", progressCallback);
};

// admin user listing
export const adminGetUsers = (search: string = "") =>
  get(`/admin/users?search=${encodeURIComponent(search)}`);

export type AdminUploadInitPage = {
  page_id: string;
  page_number: number;
  raw_data: unknown;
  ext: string | null;
};

export type AdminUploadInitDocument = {
  document_id: string;
  document_name: string;
  pages: AdminUploadInitPage[];
};

export type AdminUploadInitResponse = {
  documents: Array<{
    document_id: string;
    document_name: string;
    pages: Array<{
      page_id: string;
      page_number: number;
      ext: string | null;
      upload_url: string | null;
      content_type: string | null;
    }>;
  }>;
};

export const adminUploadFolderInit = (
  targetType: "user" | "organisation" | "folder",
  targetId: string,
  documents: AdminUploadInitDocument[],
) =>
  post("/admin/upload/folder/init", {
    target_type: targetType,
    target_id: targetId,
    documents,
  });

export const adminUploadFolderFinalize = (
  documentIds: string[],
  opts: {
    notify?: boolean;
    targetType?: "user" | "organisation" | "folder";
    targetId?: string;
  } = {},
) =>
  post("/admin/upload/folder/finalize", {
    document_ids: documentIds,
    notify: opts.notify ?? true,
    target_type: opts.targetType,
    target_id: opts.targetId,
  });

// admin staging folders
export const adminGetFolders = () => get("/admin/folders");

export const adminCreateFolder = (name: string) => {
  const form = new FormData();
  form.append("name", name);
  return post("/admin/folders", form, true, "multipart/form-data");
};

export const adminDeleteFolder = (folderId: string) =>
  del(`/admin/folders/${folderId}`);

export const adminGetFolderDocuments = (
  folderId: string,
  opts?: { limit?: number; offset?: number; search?: string; status?: "pending" | "ready" | "validated" | "delivered" },
) => {
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.offset != null) params.set("offset", String(opts.offset));
  if (opts?.search) params.set("search", opts.search);
  if (opts?.status) params.set("status", opts.status);
  const qs = params.toString();
  return get(`/admin/folders/${folderId}/documents${qs ? `?${qs}` : ""}`);
};

export const adminUpdateDocumentStatus = (documentId: string, status: "pending" | "ready" | "validated") => {
  const form = new FormData();
  form.append("status", status);
  return patch(`/admin/document/${documentId}/status`, form, true, "multipart/form-data");
};

export const adminAssignFolder = (
  folderId: string,
  targetType: "user" | "organisation",
  targetId: string,
  notify: boolean = true,
) => {
  const form = new FormData();
  form.append("target_type", targetType);
  form.append("target_id", targetId);
  form.append("notify", String(notify));
  return post(`/admin/folders/${folderId}/assign`, form, true, "multipart/form-data");
};

// chemistry stats endpoints
export const getMoleculeStats = () => get("/document/stats/molecules");
export const getReactionStats = () => get("/document/stats/reactions");

export const getAllMolecules = (
  params: { limit?: number; offset?: number; search?: string; document_id?: string; source?: string; organisation_id?: string } = {},
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.append("limit", String(params.limit));
  if (params.offset !== undefined) query.append("offset", String(params.offset));
  if (params.search) query.append("search", params.search);
  if (params.document_id) query.append("document_id", params.document_id);
  if (params.source) query.append("source", params.source);
  if (params.organisation_id) query.append("organisation_id", params.organisation_id);
  return get(
    `/document/molecules/list${query.toString() ? `?${query.toString()}` : ""}`,
    true,
    "application/json",
    signal,
  );
};

export const getAllReactions = (
  params: { limit?: number; offset?: number; document_id?: string; source?: string; organisation_id?: string } = {},
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.append("limit", String(params.limit));
  if (params.offset !== undefined) query.append("offset", String(params.offset));
  if (params.document_id) query.append("document_id", params.document_id);
  if (params.source) query.append("source", params.source);
  if (params.organisation_id) query.append("organisation_id", params.organisation_id);
  return get(
    `/document/reactions/list${query.toString() ? `?${query.toString()}` : ""}`,
    true,
    "application/json",
    signal,
  );
};

// comment endpoints
export const getDocumentComments = (documentId: string) =>
  get(`/document/${documentId}/comments/`);

export const createComment = (
  documentId: string,
  body: {
    page_id: string;
    text: string;
    x_position: number;
    y_position: number;
    parent_id?: string;
  },
) => post(`/document/${documentId}/comments/`, body);

export const deleteComment = (documentId: string, commentId: string) =>
  del(`/document/${documentId}/comments/${commentId}/`);

// admin endpoints
export const getAdminUserStats = (range: string = "1M") =>
  get(`/admin/stats/users?range=${range}`);
export const getAdminOrgStats = (range: string = "1M") =>
  get(`/admin/stats/organizations?range=${range}`);
export const getAdminDocumentStats = () => get("/admin/stats/documents");
export const getAdminAccountStats = () => get("/admin/stats/accounts");
export const getAdminActiveUsersStats = () => get("/admin/stats/active-users");
export const getAllDocumentsAdmin = (search: string = "") =>
  get(`/admin/documents${search ? `?search=${encodeURIComponent(search)}` : ""}`);
